import { useState, useEffect } from 'react'
import { initLiff, closeLiffWindow } from './lib/liff'
import { liffApi, type LiffAuth, type LiffMenu, type LiffSlot, type LiffReservation } from './lib/api'

type Screen =
  | { name: 'home' }
  | { name: 'reserve-menu' }
  | { name: 'reserve-date' }
  | { name: 'reserve-slot' }
  | { name: 'reserve-confirm' }
  | { name: 'reserve-done', reservationId: string, startAt: string }

interface BookingDraft {
  menuIds: string[]
  date: string
  slot: LiffSlot | null
  customerNote: string
}

const EMPTY_DRAFT: BookingDraft = { menuIds: [], date: '', slot: null, customerNote: '' }

function todayJst(): string {
  const d = new Date()
  const jst = new Date(d.getTime() + 9 * 60 * 60_000)
  return jst.toISOString().slice(0, 10)
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  const jst = new Date(d.getTime() + 9 * 60 * 60_000)
  return `${String(jst.getUTCHours()).padStart(2, '0')}:${String(jst.getUTCMinutes()).padStart(2, '0')}`
}

function formatDateLong(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00+09:00`)
  const jst = new Date(d.getTime() + 9 * 60 * 60_000)
  const dow = ['日', '月', '火', '水', '木', '金', '土'][jst.getUTCDay()]
  return `${jst.getUTCMonth() + 1}月${jst.getUTCDate()}日(${dow})`
}

function readTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('token')
}

export default function App() {
  // Webhook-issued token (carries authoritative tenant_id). Read once on first mount.
  const [token] = useState<string | null>(readTokenFromUrl)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [screen, setScreen] = useState<Screen>({ name: 'home' })
  const [draft, setDraft] = useState<BookingDraft>(EMPTY_DRAFT)

  useEffect(() => {
    // When token is present, skip the LIFF id_token round-trip — the token already
    // identifies friend + tenant, and id_token resolution is what causes cross-tenant
    // bleed when the LIFF app is shared across multiple Messaging API channels.
    if (token) {
      setAuthReady(true)
      return
    }
    initLiff()
      .then((t) => { setIdToken(t); setAuthReady(true) })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e)
        if (!msg.includes('Redirecting')) setAuthError(msg)
      })
  }, [token])

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-red-600 text-sm mb-2">認証エラー</p>
          <p className="text-xs text-gray-500">{authError}</p>
        </div>
      </div>
    )
  }
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400 text-sm">読み込み中…</p>
      </div>
    )
  }

  const auth: LiffAuth = { token, idToken }

  const startBooking = () => {
    setDraft(EMPTY_DRAFT)
    setScreen({ name: 'reserve-menu' })
  }

  return (
    <div className="min-h-screen pb-20">
      {screen.name === 'home' && (
        <HomeScreen auth={auth} onStartBooking={startBooking} />
      )}
      {screen.name === 'reserve-menu' && (
        <ReserveMenuScreen
          auth={auth}
          initial={draft.menuIds}
          onCancel={() => setScreen({ name: 'home' })}
          onNext={(menuIds) => {
            setDraft({ ...draft, menuIds, date: draft.date || todayJst() })
            setScreen({ name: 'reserve-date' })
          }}
        />
      )}
      {screen.name === 'reserve-date' && (
        <ReserveDateScreen
          initial={draft.date}
          onBack={() => setScreen({ name: 'reserve-menu' })}
          onNext={(date) => {
            setDraft({ ...draft, date })
            setScreen({ name: 'reserve-slot' })
          }}
        />
      )}
      {screen.name === 'reserve-slot' && (
        <ReserveSlotScreen
          auth={auth}
          date={draft.date}
          menuIds={draft.menuIds}
          onBack={() => setScreen({ name: 'reserve-date' })}
          onNext={(slot) => {
            setDraft({ ...draft, slot })
            setScreen({ name: 'reserve-confirm' })
          }}
        />
      )}
      {screen.name === 'reserve-confirm' && draft.slot && (
        <ReserveConfirmScreen
          auth={auth}
          draft={draft}
          onChangeNote={(customerNote) => setDraft({ ...draft, customerNote })}
          onBack={() => setScreen({ name: 'reserve-slot' })}
          onDone={(id) => setScreen({ name: 'reserve-done', reservationId: id, startAt: draft.slot!.startAt })}
        />
      )}
      {screen.name === 'reserve-done' && (
        <ReserveDoneScreen
          startAt={screen.startAt}
          onBackToHome={() => setScreen({ name: 'home' })}
        />
      )}
    </div>
  )
}

// ─── Home ────────────────────────────────────────────

function HomeScreen({ auth, onStartBooking }: { auth: LiffAuth; onStartBooking: () => void }) {
  const [reservations, setReservations] = useState<LiffReservation[] | null>(null)
  const [error, setError] = useState('')

  const load = () => {
    liffApi.myReservations(auth).then((res) => {
      if (res.success) setReservations(res.data)
      else setError(res.error)
    }).catch((e) => setError(e instanceof Error ? e.message : 'load failed'))
  }
  useEffect(load, [auth.token, auth.idToken])

  const cancel = async (id: string) => {
    if (!confirm('この予約をキャンセルしますか？')) return
    const res = await liffApi.cancel(auth, id)
    if (!res.success) {
      alert(res.error)
      return
    }
    load()
  }

  const upcoming = reservations?.filter((r) => r.status === 'confirmed' || r.status === 'visited') ?? []
  const past = reservations?.filter((r) => ['completed', 'cancelled', 'no_show'].includes(r.status)) ?? []

  return (
    <div>
      <header className="px-4 pt-6 pb-4 bg-white border-b border-gray-200 sticky top-0 z-10">
        <h1 className="text-xl font-bold text-gray-900">マイ予約</h1>
      </header>

      <div className="p-4 space-y-4">
        {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {reservations === null ? (
          <p className="text-center text-gray-400 text-sm py-8">読み込み中…</p>
        ) : (
          <>
            <section>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">直近の予約</h2>
              {upcoming.length === 0 ? (
                <div className="rounded-lg bg-white p-4 text-center text-gray-400 text-sm">
                  予約はまだありません
                </div>
              ) : (
                <div className="space-y-2">
                  {upcoming.map((r) => (
                    <ReservationCard key={r.id} r={r} onCancel={cancel} />
                  ))}
                </div>
              )}
            </section>

            {past.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">過去の予約</h2>
                <div className="space-y-2 opacity-60">
                  {past.slice(0, 5).map((r) => (
                    <ReservationCard key={r.id} r={r} onCancel={cancel} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* 固定下部CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={onStartBooking}
          className="w-full py-3.5 rounded-xl text-white font-semibold text-base"
          style={{ backgroundColor: 'var(--line-green)' }}
        >
          ＋ 新しく予約する
        </button>
      </div>
    </div>
  )
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: '確定',
  visited: '来店済',
  completed: '完了',
  cancelled: 'キャンセル',
  no_show: '無断キャンセル',
}
const STATUS_COLOR: Record<string, string> = {
  confirmed: 'bg-blue-100 text-blue-700',
  visited: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-200 text-gray-600',
  no_show: 'bg-red-100 text-red-700',
}

function ReservationCard({ r, onCancel }: { r: LiffReservation; onCancel: (id: string) => void }) {
  const date = r.startAt.slice(0, 10)
  return (
    <div className="rounded-lg bg-white border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {formatDateLong(date)}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {formatTime(r.startAt)}〜{formatTime(r.endAt)}（{r.totalMinutes}分）
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${STATUS_COLOR[r.status] ?? ''}`}>
          {STATUS_LABEL[r.status] ?? r.status}
        </span>
      </div>
      <div className="mt-2 text-xs text-gray-600">
        {r.menus.map((m) => m.name).join(' + ')}
      </div>
      <div className="mt-1 text-xs text-gray-500 tabular-nums">¥{r.totalPrice.toLocaleString()}</div>
      {r.cancellable && (
        <button
          onClick={() => onCancel(r.id)}
          className="mt-3 text-xs text-red-500"
        >
          キャンセルする
        </button>
      )}
    </div>
  )
}

// ─── Step 1: Menu ────────────────────────────────────

function ReserveMenuScreen({
  auth,
  initial,
  onCancel,
  onNext,
}: {
  auth: LiffAuth
  initial: string[]
  onCancel: () => void
  onNext: (menuIds: string[]) => void
}) {
  const [menus, setMenus] = useState<LiffMenu[]>([])
  const [selected, setSelected] = useState<string[]>(initial)
  const [error, setError] = useState('')

  useEffect(() => {
    liffApi.menus(auth).then((res) => {
      if (res.success) setMenus(res.data)
      else setError(res.error)
    })
  }, [auth.token, auth.idToken])

  const toggle = (id: string) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  const totalMinutes = menus.filter((m) => selected.includes(m.id)).reduce((s, m) => s + m.durationMinutes, 0)
  const totalPrice = menus.filter((m) => selected.includes(m.id)).reduce((s, m) => s + m.price, 0)

  return (
    <div>
      <header className="px-4 pt-6 pb-4 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button onClick={onCancel} className="text-sm text-gray-500 mb-1">＜ 戻る</button>
        <h1 className="text-lg font-bold text-gray-900">メニュー選択</h1>
        <p className="text-xs text-gray-500 mt-0.5">複数選択できます</p>
      </header>

      <div className="p-4 space-y-2">
        {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
        {menus.length === 0 && !error && (
          <p className="text-center text-gray-400 text-sm py-8">読み込み中…</p>
        )}
        {menus.map((m) => {
          const checked = selected.includes(m.id)
          return (
            <button
              key={m.id}
              onClick={() => toggle(m.id)}
              className={`w-full text-left rounded-xl border p-4 transition-colors ${
                checked ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
              style={{ minHeight: '60px' }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">{m.name}</div>
                  {m.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>
                  )}
                </div>
                <div className="text-right text-xs tabular-nums shrink-0">
                  <div className="font-medium text-gray-700">{m.durationMinutes}分</div>
                  <div className="text-gray-500">¥{m.price.toLocaleString()}</div>
                </div>
                {checked && (
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs" style={{ backgroundColor: 'var(--line-green)' }}>✓</span>
                )}
              </div>
            </button>
          )
        })}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        {selected.length > 0 && (
          <div className="text-xs text-gray-600 text-center mb-2 tabular-nums">
            合計 {totalMinutes}分 / ¥{totalPrice.toLocaleString()}
          </div>
        )}
        <button
          disabled={selected.length === 0}
          onClick={() => onNext(selected)}
          className="w-full py-3.5 rounded-xl text-white font-semibold text-base disabled:opacity-40"
          style={{ backgroundColor: 'var(--line-green)' }}
        >
          次へ（日付を選ぶ）
        </button>
      </div>
    </div>
  )
}

// ─── Step 2: Date ────────────────────────────────────

function ReserveDateScreen({
  initial,
  onBack,
  onNext,
}: {
  initial: string
  onBack: () => void
  onNext: (date: string) => void
}) {
  const [date, setDate] = useState(initial || todayJst())
  const today = todayJst()

  return (
    <div>
      <header className="px-4 pt-6 pb-4 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button onClick={onBack} className="text-sm text-gray-500 mb-1">＜ 戻る</button>
        <h1 className="text-lg font-bold text-gray-900">日付選択</h1>
      </header>

      <div className="p-4">
        <input
          type="date"
          value={date}
          min={today}
          onChange={(e) => setDate(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
        />
        <p className="text-xs text-gray-500 mt-2">
          選択した日: {formatDateLong(date)}
        </p>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={() => onNext(date)}
          className="w-full py-3.5 rounded-xl text-white font-semibold text-base"
          style={{ backgroundColor: 'var(--line-green)' }}
        >
          次へ（時間を選ぶ）
        </button>
      </div>
    </div>
  )
}

// ─── Step 3: Slot ────────────────────────────────────

function ReserveSlotScreen({
  auth,
  date,
  menuIds,
  onBack,
  onNext,
}: {
  auth: LiffAuth
  date: string
  menuIds: string[]
  onBack: () => void
  onNext: (slot: LiffSlot) => void
}) {
  const [slots, setSlots] = useState<LiffSlot[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    liffApi.slots(auth, date, menuIds).then((res) => {
      if (res.success) setSlots(res.data)
      else setError(res.error)
    }).catch((e) => setError(e instanceof Error ? e.message : 'failed')).finally(() => setLoading(false))
  }, [auth.token, auth.idToken, date, menuIds])

  return (
    <div>
      <header className="px-4 pt-6 pb-4 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button onClick={onBack} className="text-sm text-gray-500 mb-1">＜ 戻る</button>
        <h1 className="text-lg font-bold text-gray-900">時間を選ぶ</h1>
        <p className="text-xs text-gray-500 mt-0.5">{formatDateLong(date)}</p>
      </header>

      <div className="p-4">
        {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
        {loading ? (
          <p className="text-center text-gray-400 text-sm py-8">空き枠を確認中…</p>
        ) : slots.length === 0 ? (
          <p className="text-center text-gray-500 text-sm py-8">この日は予約できません</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {slots.map((s) => (
              <button
                key={s.startAt}
                disabled={!s.available}
                onClick={() => onNext(s)}
                className="py-3 rounded-lg text-sm font-semibold tabular-nums border disabled:bg-gray-50 disabled:text-gray-300 disabled:border-gray-200"
                style={s.available ? { borderColor: 'var(--line-green)', color: 'var(--line-green)' } : undefined}
              >
                {formatTime(s.startAt)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Step 4: Confirm ─────────────────────────────────

function ReserveConfirmScreen({
  auth,
  draft,
  onChangeNote,
  onBack,
  onDone,
}: {
  auth: LiffAuth
  draft: BookingDraft
  onChangeNote: (note: string) => void
  onBack: () => void
  onDone: (id: string) => void
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!draft.slot) return
    setSaving(true)
    setError('')
    try {
      const res = await liffApi.create(auth, {
        startAt: draft.slot.startAt,
        menuIds: draft.menuIds,
        customerNote: draft.customerNote || undefined,
      })
      if (!res.success) {
        setError(res.error)
        return
      }
      onDone(res.data.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  if (!draft.slot) return null

  return (
    <div>
      <header className="px-4 pt-6 pb-4 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button onClick={onBack} className="text-sm text-gray-500 mb-1">＜ 戻る</button>
        <h1 className="text-lg font-bold text-gray-900">予約内容の確認</h1>
      </header>

      <div className="p-4 space-y-3">
        <div className="rounded-xl bg-white border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">日時</div>
          <div className="text-sm font-semibold tabular-nums">
            {formatDateLong(draft.date)} {formatTime(draft.slot.startAt)}〜{formatTime(draft.slot.endAt)}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-2">メニュー（{draft.menuIds.length}点）</div>
          <div className="text-sm">予約完了画面に詳細が表示されます</div>
        </div>

        <label className="block">
          <span className="text-xs text-gray-500">ご要望（任意）</span>
          <textarea
            value={draft.customerNote}
            onChange={(e) => onChangeNote(e.target.value)}
            rows={3}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="例: 前回と同じカラー希望"
          />
        </label>

        {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={submit}
          disabled={saving}
          className="w-full py-3.5 rounded-xl text-white font-semibold text-base disabled:opacity-50"
          style={{ backgroundColor: 'var(--line-green)' }}
        >
          {saving ? '送信中…' : 'この内容で予約する'}
        </button>
      </div>
    </div>
  )
}

// ─── Step 5: Done ────────────────────────────────────

function ReserveDoneScreen({ startAt, onBackToHome }: { startAt: string; onBackToHome: () => void }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-white text-3xl mb-4"
        style={{ backgroundColor: 'var(--line-green)' }}
      >
        ✓
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">予約が確定しました</h2>
      <p className="text-sm text-gray-600 tabular-nums">
        {formatDateLong(startAt.slice(0, 10))} {formatTime(startAt)}〜
      </p>
      <p className="text-xs text-gray-500 mt-3">
        確認メッセージをLINEで送信しました
      </p>
      <div className="mt-8 flex flex-col gap-2 w-full max-w-xs">
        <button
          onClick={onBackToHome}
          className="w-full py-3 rounded-xl text-white font-semibold"
          style={{ backgroundColor: 'var(--line-green)' }}
        >
          マイ予約へ
        </button>
        <button
          onClick={closeLiffWindow}
          className="w-full py-3 rounded-xl border border-gray-300 text-gray-700 font-medium"
        >
          LINEに戻る
        </button>
      </div>
    </div>
  )
}
